import { useMutation, UseMutationResult, UseMutationOptions, MutationKey } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

export type MutationFnVariables<TPayload = void> = {
  url: string;
  body?: TPayload;
};

export type GetMutationFnParams = {
  method?: RequestInit['method'];
  bodyType?: 'json' | 'text';
  errorType?: 'text';
  requestHeaders?: Record<string, string>;
};

export function getMutationFn<TMutationResult, TPayload = void>({
  method = 'POST',
  bodyType = 'json',
  errorType,
  requestHeaders = {}
}: GetMutationFnParams = {}) {
  return async ({ url, body }: MutationFnVariables<TPayload>) => {
    const response = await fetch(url, {
      method,
      headers: {
        'X-REQUEST-ID': uuidv4(),
        'Content-Type': 'application/json',
        ...requestHeaders
      },
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) {
      const error = errorType === 'text' ? await response.text() : response.statusText;
      throw new Error(error);
    }
    if (response.status === 204 || bodyType === 'text') return response.text() as Promise<TMutationResult>;
    return response.json() as Promise<TMutationResult>;
  };
}

export type FetchMutationParams<TMutationResult, TVariables, TError = Error> = GetMutationFnParams & {
  mutationKey: MutationKey;
  url: string;
  options?: Omit<UseMutationOptions<TMutationResult, TError, TVariables>, 'mutationKey' | 'mutationFn'>;
};

// returns mutation options
export function getFetchMutation<TMutationResult, TVariables, TError = Error>({
  mutationKey,
  url,
  method,
  bodyType,
  requestHeaders,
  options = {}
}: FetchMutationParams<TMutationResult, TVariables, TError>): UseMutationOptions<TMutationResult, TError, TVariables> {
  const mutationFn = getMutationFn<TMutationResult, TVariables>({ method, bodyType, requestHeaders });
  return { mutationKey, mutationFn: (body) => mutationFn({ url, body }), ...options };
}

// returns hook to use in component
export function useFetchMutation<TMutationResult, TVariables = void, TError = Error>(
  params: FetchMutationParams<TMutationResult, TVariables, TError>
): UseMutationResult<TMutationResult, TError, TVariables> {
  const mutationOptions = getFetchMutation<TMutationResult, TVariables, TError>(params);
  return useMutation<TMutationResult, TError, TVariables>(mutationOptions);
}
